import { flow, getParent, getSnapshot, types } from 'mobx-state-tree';
import { arrayIncludes } from '../utils';

const VariantModel = types.model({
    externalId: types.number,
    isDefault: types.optional(types.boolean, false),
    options: types.array(
        types.model({
            position: types.number,
            value: types.string
        })
    ),
    title: types.string,
    image: types.maybeNull(types.number),
    quantity: types.number,
    strictQuantity: types.maybeNull(types.number),
    price: types.number,
    originalPrice: types.maybeNull(types.number),
    inCart: types.optional(types.boolean, false),
    cashbackPrice: types.maybeNull(types.number), // For Distribution Partners only
    cashbackType: types.maybeNull(types.string), // For Distribution Partners only
    cashbackDetails: types.maybeNull(
        types.array(
            types.model({
                // For Distribution Partners only
                name: types.string,
                value: types.number
            })
        )
    ),
    pointsDetails: types.maybeNull(
        types.model({
            maxPoints: types.number,
            isWelcome: types.boolean,
            list: types.array(
                types.model({
                    type: types.string,
                    value: types.number
                })
            )
        })
    ),
    rewards: types.maybe(
        types.maybeNull(
            types.array(
                types.model({
                    _id: types.maybe(types.maybeNull(types.string)),
                    title: types.maybe(types.maybeNull(types.string)),
                    description: types.maybe(types.maybeNull(types.string)),
                    image: types.maybe(types.maybeNull(types.string)),
                    expireAt: types.maybe(types.maybeNull(types.string)),
                    appliedTo: types.maybe(types.maybeNull(types.string)),
                    amount: types.maybe(types.maybeNull(types.number)),
                    rewardType: types.maybe(types.maybeNull(types.string)),
                    value: types.maybe(types.maybeNull(types.number))
                })
            )
        )
    ),
    priceDetails: types.maybe(
        types.maybeNull(
            types.model({
                originalPrice: types.maybeNull(types.number),
                rewardsPrice: types.maybeNull(types.number),
                rewardsPercent: types.maybeNull(types.number),
                youPayPrice: types.maybeNull(types.number)
            })
        )
    )
});

export const VariantsStore = types
    .model('VariantsStore', {
        list: types.array(VariantModel),
        loading: false,
        qty: 1
    })
    .views((self) => ({
        get product() {
            return getParent(self);
        },
        get originalPrice() {
            if (
                self.currentVariantData?.priceDetails?.originalPrice ||
                self.currentVariantData?.priceDetails?.originalPrice === 0
            ) {
                return self.currentVariantData?.priceDetails?.originalPrice;
            }
            return self.currentVariantData?.originalPrice;
        },
        get youPayPrice() {
            if (
                self.currentVariantData?.priceDetails?.youPayPrice ||
                self.currentVariantData?.priceDetails?.youPayPrice === 0
            ) {
                return self.currentVariantData?.priceDetails?.youPayPrice;
            }
            return self.currentVariantData?.price;
        },
        get currentVariantData() {
            const currentVariant = self.currentVariant;
            const data = [...getSnapshot(self.list)];
            return data.find((variant) => variant.externalId === currentVariant.externalId);
        },
        get selectedVariant() {
            if (self.product.options.allOptionsSelected) {
                const selectedOptions = self.product.options.selectedOptions;
                const index = self.list.findIndex((variant) => {
                    return arrayIncludes(variant.options, selectedOptions);
                });
                return self.list[index];
            }
            return null;
        },
        get currentVariant() {
            if (self.selectedVariant) {
                return self.selectedVariant;
            }
            return self.list[0];
        },
        get minQty() {
            if (self.currentVariant.strictQuantity) {
                return self.currentVariant.strictQuantity;
            }

            return 1;
        },
        get maxQty() {
            if (self.currentVariant.strictQuantity) {
                return self.currentVariant.strictQuantity;
            }

            let variantQuantity = 0;
            if (self.selectedVariant) {
                variantQuantity = self.selectedVariant.quantity;
            } else {
                variantQuantity = Math.max(...self.list.map(({ quantity }) => quantity));
            }
            return Math.max(variantQuantity, 1);
        }
    }))
    .actions((self) => ({
        onUpdateRewards: flow(function* onUpdateRewards() {
            if (typeof self.product.root.callbacks.onCalculatePromotionRewards !== 'function') return null;

            self.loading = true;

            try {
                const response = yield self.product.root.callbacks.onCalculatePromotionRewards({ qty: self.qty });

                if (!response || !Array.isArray(response.variants)) return null;

                response.variants.forEach((variantFromResponse) => {
                    const variant = self.list.find((v) => v.externalId === variantFromResponse.externalId);
                    if (variant) {
                        variant.cashbackPrice = variantFromResponse.cashbackPrice ?? null;
                        variant.cashbackType = variantFromResponse.cashbackType ?? null;
                        variant.cashbackDetails = variantFromResponse.cashbackDetails ?? null;
                    }
                });
            } catch (err) {
                console.error('Failed to load ', err); // eslint-disable-line
            } finally {
                self.loading = false;
            }
        }),
        setQty(value) {
            self.qty = value;
        },
        incrementQty: flow(function* incrementQty() {
            self.qty += 1;
            if (self.qty > self.maxQty) {
                self.qty = self.maxQty;
            }

            yield self.onUpdateRewards();
        }),
        decrementQty: flow(function* decrementQty() {
            self.qty -= 1;
            if (self.qty < 1) {
                self.qty = 1;
            }

            yield self.onUpdateRewards();
        }),
        setVariants(variants) {
            self.list = variants;
        },
        checkStrictQty() {
            if (self.currentVariant.strictQuantity) {
                self.setQty(self.currentVariant.strictQuantity);
            }
        }
    }));
